/**
 * WizardProductsTable - Grid editável de Produtos e Serviços no Step 2 do wizard
 *
 * Espelha o layout do DealItemsSummary (aba Produtos do modal de negócio):
 *  - Seção "Produtos": Produto, SKU, Unidade, Qtd, Preço, Desconto, Total
 *  - Seção "Serviços": Produto, Unidade, Qtd, Preço, Mensalidade, Prazo, Desconto, Total
 *  - Rodapé com mini-cards: Subtotal, Descontos, Total, MRR, Vigência (clean underline accent)
 *
 * Edição inline permanece em qtd/preço/desconto via InlineEditableField.
 * Campos vindos do produto (mensalidade, periodicidade, prazo, unidade) são read-only
 * — pertencem ao cadastro do produto, não ao item do deal.
 *
 * @release v2.0.0 - 2026-05-19 — Redesign Produtos x Serviços + totalizadores
 */
import React, { useMemo } from 'react';
import { Package, Wrench, ShoppingCart, Percent, Calculator, RefreshCw, CalendarClock } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { InlineEditableField } from '@/components/crm/deals/modal/tabs/products/InlineEditableField';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/currency-utils';
import type { WizardDealItem } from '@/types/contracts';
import {
  formatRecurrenceLabel,
  formatTermLabel,
  calculateRecurringTotal,
  isRecurringService,
  type RecurrenceInterval,
  type TermType,
  type TermUnit,
} from '@/lib/crm/recurringServiceUtils';

interface WizardProductsTableProps {
  items: WizardDealItem[];
  onChangeItems: (items: WizardDealItem[]) => void;
}

/** Recalcula o total individual após edição de qtd/preço/desconto */
function recomputeItemTotal(item: WizardDealItem): WizardDealItem {
  const total = item.quantity * item.unit_price - (item.discount || 0);
  // Recalcula vigência somente para serviços recorrentes
  const lifetime = isRecurringService(item.product_type, item.unit_type)
    ? calculateRecurringTotal({
        recurring_price: item.recurring_price,
        recurrence_interval: item.recurrence_interval as RecurrenceInterval | null,
        term_type: item.term_type as TermType | null,
        term_quantity: item.term_quantity,
        term_unit: item.term_unit as TermUnit | null,
      })
    : null;
  return {
    ...item,
    total,
    lifetime_total: lifetime ? lifetime * item.quantity : null,
  };
}

/**
 * Mini-card de totalizador no padrão "clean underline accent" do DealItemsSummary
 */
function TotalCard({
  label,
  value,
  icon: Icon,
  accentClass,
  emphasis,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  accentClass: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex-1 min-w-[145px] p-3 rounded-lg bg-card border border-border border-t-2 transition-colors',
        accentClass,
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <Icon className="h-3 w-3 text-muted-foreground" />
      </div>
      <div className={cn('text-xl', emphasis ? 'font-extrabold' : 'font-bold', 'tabular-nums')}>
        {value}
      </div>
    </div>
  );
}

export function WizardProductsTable({ items, onChangeItems }: WizardProductsTableProps) {
  // Separa itens por tipo (mesma lógica do DealItemsSummary)
  const { produtos, servicos } = useMemo(() => {
    const produtos: WizardDealItem[] = [];
    const servicos: WizardDealItem[] = [];
    items.forEach((it) => {
      if (it.product_type === 'service') servicos.push(it);
      else produtos.push(it);
    });
    return { produtos, servicos };
  }, [items]);

  // Totalizadores agregados
  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    const descontos = items.reduce((s, i) => s + (i.discount || 0), 0);
    const total = items.reduce((s, i) => s + (i.total || 0), 0);
    const mrr = items.reduce((s, i) => {
      if (!isRecurringService(i.product_type, i.unit_type)) return s;
      return s + (i.recurring_price || 0) * (i.quantity || 1);
    }, 0);
    const lifetime = items.reduce((s, i) => s + (i.lifetime_total || 0), 0);
    const hasRecurring = items.some((i) => isRecurringService(i.product_type, i.unit_type));
    return { subtotal, descontos, total, mrr, lifetime, hasRecurring };
  }, [items]);

  /** Atualiza um item específico pelo id e recalcula derivados */
  const updateItem = (id: string, patch: Partial<WizardDealItem>) => {
    onChangeItems(
      items.map((it) => (it.id === id ? recomputeItemTotal({ ...it, ...patch }) : it)),
    );
  };

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        Este negócio não possui produtos/serviços cadastrados.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---------- PRODUTOS ---------- */}
      {produtos.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Package className="h-3.5 w-3.5 text-amber-500" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Produtos ({produtos.length})
            </p>
          </div>
          {/* Wrapper com scroll horizontal robusto: w-max no <table> força largura mínima
              somada das colunas, disparando overflow-x-auto sempre que necessário */}
          <div className="border rounded-md overflow-x-auto overflow-y-hidden">
            <Table className="w-max min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px] whitespace-nowrap">Produto</TableHead>
                  <TableHead className="min-w-[90px] whitespace-nowrap">SKU</TableHead>
                  <TableHead className="min-w-[90px] whitespace-nowrap">Unidade</TableHead>
                  <TableHead className="min-w-[80px] text-right whitespace-nowrap">Qtd</TableHead>
                  <TableHead className="min-w-[110px] text-right whitespace-nowrap">Preço</TableHead>
                  <TableHead className="min-w-[110px] text-right whitespace-nowrap">Desconto</TableHead>
                  <TableHead className="min-w-[120px] text-right whitespace-nowrap">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {produtos.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="p-1">
                      <Input
                        value={item.product_name}
                        onChange={(e) => updateItem(item.id, { product_name: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        value={item.sku}
                        onChange={(e) => updateItem(item.id, { sku: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </TableCell>
                    <TableCell className="p-1 text-xs">
                      <Badge variant="outline" className="text-[10px] capitalize font-normal">
                        {item.unit_type || 'Un'}
                      </Badge>
                    </TableCell>
                    <TableCell className="p-1">
                      <InlineEditableField
                        value={item.quantity}
                        onChange={(v) => updateItem(item.id, { quantity: v })}
                        min={1}
                        allowDecimals={false}
                        className="h-8 text-xs text-right"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <InlineEditableField
                        value={item.unit_price}
                        onChange={(v) => updateItem(item.id, { unit_price: v })}
                        min={0}
                        className="h-8 text-xs text-right"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <InlineEditableField
                        value={item.discount}
                        onChange={(v) => updateItem(item.id, { discount: v })}
                        min={0}
                        className="h-8 text-xs text-right"
                      />
                    </TableCell>
                    <TableCell className="p-1 text-right text-xs font-medium pr-3 tabular-nums">
                      {formatCurrency(item.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ---------- SERVIÇOS ---------- */}
      {servicos.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Wrench className="h-3.5 w-3.5 text-blue-500" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Serviços ({servicos.length})
            </p>
          </div>
          {/* Wrapper com scroll horizontal robusto (idem Produtos) */}
          <div className="border rounded-md overflow-x-auto overflow-y-hidden">
            <Table className="w-max min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px] whitespace-nowrap">Serviço</TableHead>
                  <TableHead className="min-w-[100px] whitespace-nowrap">Unidade</TableHead>
                  <TableHead className="min-w-[80px] text-right whitespace-nowrap">Qtd</TableHead>
                  <TableHead className="min-w-[110px] text-right whitespace-nowrap">Preço</TableHead>
                  <TableHead className="min-w-[140px] text-right whitespace-nowrap">Mensalidade</TableHead>
                  <TableHead className="min-w-[110px] whitespace-nowrap">Prazo</TableHead>
                  <TableHead className="min-w-[110px] text-right whitespace-nowrap">Desconto</TableHead>
                  <TableHead className="min-w-[120px] text-right whitespace-nowrap">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {servicos.map((item) => {
                  const isRec = isRecurringService(item.product_type, item.unit_type);
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="p-1">
                        <Input
                          value={item.product_name}
                          onChange={(e) => updateItem(item.id, { product_name: e.target.value })}
                          className="h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <Badge variant="outline" className="text-[10px] capitalize font-normal">
                          {item.unit_type || 'Avulso'}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-1">
                        <InlineEditableField
                          value={item.quantity}
                          onChange={(v) => updateItem(item.id, { quantity: v })}
                          min={1}
                          allowDecimals={false}
                          className="h-8 text-xs text-right"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <InlineEditableField
                          value={item.unit_price}
                          onChange={(v) => updateItem(item.id, { unit_price: v })}
                          min={0}
                          className="h-8 text-xs text-right"
                        />
                      </TableCell>
                      <TableCell className="p-1 text-right text-xs text-muted-foreground tabular-nums">
                        {isRec && item.recurring_price ? (
                          <span className="font-medium text-foreground">
                            {formatCurrency(item.recurring_price)} /{' '}
                            {formatRecurrenceLabel(item.recurrence_interval as RecurrenceInterval | null)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="p-1 text-xs text-muted-foreground">
                        {isRec
                          ? formatTermLabel(
                              item.term_type as TermType | null,
                              item.term_quantity ?? null,
                              item.term_unit as TermUnit | null,
                            ) || '—'
                          : '—'}
                      </TableCell>
                      <TableCell className="p-1">
                        <InlineEditableField
                          value={item.discount}
                          onChange={(v) => updateItem(item.id, { discount: v })}
                          min={0}
                          className="h-8 text-xs text-right"
                        />
                      </TableCell>
                      <TableCell className="p-1 text-right text-xs font-medium pr-3 tabular-nums">
                        {formatCurrency(item.total)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ---------- TOTALIZADORES (mesmo visual da aba Produtos) ---------- */}
      <div className="flex flex-wrap gap-2 pt-1">
        <TotalCard
          label="Subtotal"
          value={formatCurrency(totals.subtotal)}
          icon={ShoppingCart}
          accentClass="border-t-muted-foreground/30"
        />
        <TotalCard
          label="Descontos"
          value={`-${formatCurrency(totals.descontos)}`}
          icon={Percent}
          accentClass="border-t-orange-500/70"
        />
        <TotalCard
          label="Total"
          value={formatCurrency(totals.total)}
          icon={Calculator}
          accentClass="border-t-foreground"
          emphasis
        />
        {totals.hasRecurring && (
          <>
            <TotalCard
              label="Total Mensal (MRR)"
              value={formatCurrency(totals.mrr)}
              icon={RefreshCw}
              accentClass="border-t-emerald-500/70"
            />
            <TotalCard
              label="Tot. Vigência Contrato"
              value={formatCurrency(totals.lifetime)}
              icon={CalendarClock}
              accentClass="border-t-blue-500/70"
            />
          </>
        )}
      </div>
    </div>
  );
}
